import { Icon } from "@mdi/react";
import React from "react";

import { Edit } from "@material-ui/icons";
import { mdiPlayBox } from "@mdi/js";
import { Button } from "antd";
import { extractYouTubeVideoId, getYouTubeThumbnailUrl } from "utils/youtubeUtils";

import { Tooltip } from "components/base";

import styles from "../../styles/tutorialViews.module.scss";
import { Category, TutorialItem } from "../../types";

interface TutorialTileViewProps {
  tutorials?: TutorialItem[];
  selectedCategory?: Category;
  /** What the user typed. When set, the category filter is not applied. */
  searchTerm?: string;
  onSelectVideo: (videoId: string) => void;
  onEdit?: (tutorialId: string) => void;
  canEdit: boolean;
}

const TutorialTileView: React.FC<TutorialTileViewProps> = ({
  tutorials = [],
  selectedCategory,
  searchTerm = "",
  onSelectVideo,
  onEdit,
  canEdit
}) => {
  // While searching, show everything that matched. Narrowing by the category
  // that happened to be selected would hide the video the user is looking for
  // whenever it lives under a different one.
  const isSearching = searchTerm.trim() !== "";
  const filteredTutorials =
    !isSearching && selectedCategory
      ? tutorials.filter((tutorial) => tutorial.categories?.includes(selectedCategory))
      : tutorials;

  const getThumbnailUrl = (videoUrl: string): string | null => {
    const videoId = extractYouTubeVideoId(videoUrl);
    return videoId ? getYouTubeThumbnailUrl(videoId) : null;
  };

  return (
    <div className={styles.tutorialTileView} data-testid="tutorial-tile-view">
      <h2>{isSearching ? `Results for “${searchTerm}”` : selectedCategory}</h2>

      <div className={styles.tileContainer}>
        {filteredTutorials.length !== 0 ? (
          filteredTutorials.map((tutorial) => (
            <div
              key={tutorial.id}
              onClick={() => onSelectVideo(tutorial.id)}
              className={styles.tile}
              style={tutorial.order ? { order: tutorial.order } : { order: 1000 }}
              data-testid={`tutorial-tile-${tutorial.id}`}>
              <div className={styles.thumbnailContainer}>
                {(() => {
                  const thumbnailUrl = getThumbnailUrl(tutorial.videoUrl);
                  return thumbnailUrl ? (
                    <img
                      src={thumbnailUrl}
                      alt={tutorial.title}
                      className={styles.thumbnail}
                    />
                  ) : (
                    <div className={`${styles.thumbnail} ${styles.placeholderThumbnail}`}>
                      <Icon path={mdiPlayBox} />
                    </div>
                  );
                })()}

                {canEdit && onEdit && (
                  <>
                    <div className={styles.videoOrderDisplay}>
                      Display Order: {tutorial.order ? tutorial.order : "Unset"}
                    </div>
                    <Tooltip title="Edit Video">
                      <Button
                        className={styles.editButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(tutorial.id);
                        }}
                        data-testid={`tutorial-tile-edit-${tutorial.id}`}>
                        <Edit fontSize="large" />
                      </Button>
                    </Tooltip>
                  </>
                )}
              </div>

              <div
                className={styles.videoTitle}
                data-testid={`tutorial-tile-title-${tutorial.id}`}>
                {tutorial.title}
              </div>
            </div>
          ))
        ) : (
          <div
            className={styles.tilePlaceholder}
            data-testid="tutorial-tiles-empty-state">
            {isSearching ? "No videos match your search" : "No Tutorial Videos Found"}
          </div>
        )}
      </div>
    </div>
  );
};

export default TutorialTileView;
